import React, { Component } from 'react';
import { Card, Row, Col, Nav, NavItem, NavLink, TabContent, TabPane, Modal, ModalBody, Button, Badge, UncontrolledButtonDropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap';
import { getAPI, deleteAPI } from '../Base/API';
import { ActiveInactiveStatusBadge, StyledCard, sortByHotelLanguage } from '../Base/Common/CommonUIComponents';
import ReactHtmlParser from 'react-html-parser';
import { FormattedMessage, injectIntl } from "react-intl";
import classnames from 'classnames';
import { canUseEmailDesigner, canUsePdfDesigner, hasPdfDesignerModule } from '../Base/Authorization';
import { handleNotification } from '../Base/Common/Notification';
import { EMAIL_TEMPLATE_TYPES } from '../Base/Common/ReferenceDataFunctions';

export class VoucherTemplates extends Component {
    static displayName = VoucherTemplates.name;
    _isMounted = false;

    constructor(props) {
        super(props);
        this.state = {
            block: true,
            blockCustom: true,
            modal: false,
            templateList: [],
            customTemplateList: [],
            activeTab: 'email',
        };
    }

    componentDidMount() {
        this._isMounted = true;
        this.getTemplates();
        this.getCustomTemplates();
    }

    componentWillUnmount() {
        this._isMounted = false;
    }

    getTemplates() {
        this.setState({ block: true });

        getAPI(result => {
            const { data, error } = result;
            if (error) {
                var errorMessage = [];
                errorMessage.push({ message: error.message, stack: error.stack, messageType: 'danger' });
                this.setState({ error: errorMessage, block: false });
                return;
            }
            this.setState({ templateList: this.groupTemplates(data ? (data.response || []) : []), block: false });
        }, '/api/Voucher/v1/Config/notificationTemplates');
    }

    //Language variants (same name, type and hotel) are shown as a single card, same aggregation as TemplatesConfig
    groupTemplates = (templates) => {
        const groups = [];

        templates.forEach(template => {
            const group = groups.find(g => g.name === template.name && g.type === template.type && g.hotelId === template.hotelId);

            if (group) {
                group.variants.push(template);
                group.active = group.active || template.active;
                group.hasStripo = group.hasStripo || !!template.templateJson;
            }
            else {
                groups.push({
                    name: template.name,
                    type: template.type,
                    hotelId: template.hotelId,
                    active: template.active,
                    hasStripo: !!template.templateJson,
                    variants: [template]
                });
            }
        });

        groups.forEach(g => g.variants.sort((a, b) => (a.languageCode || '').localeCompare(b.languageCode || '')));

        return groups;
    }

    getCustomTemplates() {
        this.setState({ blockCustom: true });

        getAPI(result => {
            const { data, error } = result;
            if (error) {
                var errorMessage = [];
                errorMessage.push({ message: error.message, stack: error.stack, messageType: 'danger' });
                this.setState({ error: errorMessage, blockCustom: false });
                return;
            }
            this.setState({ customTemplateList: data ? (data.response || []) : [], blockCustom: false });
        }, '/api/Voucher/Config/v1/voucherTemplateDesign');
    }

    //Stripo templates come back with one entry per language variant, the unpublished ones without html
    getTemplateSample = (templateId, templateName, isStripo) => {
        this.setState({ block: true });

        getAPI(result => {
            const { data, error } = result;
            if (error) {
                var errorMessage = [];
                errorMessage.push({ message: error.message, stack: error.stack, messageType: 'danger' });
                this.setState({ error: errorMessage, block: false });
                return;
            }
            if (data) {
                //Business errors are shown as a notification, the card error banner is kept for request failures
                if (data.errors && data.errors.length > 0) {
                    handleNotification(data);
                }
                else if (data.response && data.response.length > 0) {
                    const templates = sortByHotelLanguage(data.response, template => template.languageCode);

                    this.setState({
                        selectedTemplateName: templateName,
                        selectedTemplate: templates,
                        selectedTemplateIsStripo: isStripo,
                        previewTab: templates[0].languageCode,
                        modal: true,
                    });
                }
                else {
                    handleNotification({ errors: [{ message: this.props.intl.formatMessage({ id: 'VoucherTemplates.PreviewNotAvailable' }) }] });
                }
            }
            this.setState({ block: false });
        }, `/api/Voucher/v1/Notification/voucherNotification/sample/${templateId}`);
    }

    deleteCustomTemplate = (id, e) => {
        e.stopPropagation();
        if (!window.confirm(this.props.intl.formatMessage({ id: 'VoucherTemplates.ConfirmDelete' }))) return;

        deleteAPI(result => {
            const { data, error } = result;
            if (error) {
                this.setState({ error: [{ message: error.message, stack: error.stack, messageType: 'danger' }] });
                return;
            }
            handleNotification(data, this.props.intl.formatMessage({ id: 'VoucherTemplates.DeleteSuccess' }), '');
            this.getCustomTemplates();
        }, `/api/Voucher/Config/v1/voucherTemplateDesign/${id}`);
    }

    togglePreviewTab = (previewTab) => { this.setState({ previewTab }); }
    toggleModal = () => { this.setState(prev => ({ modal: !prev.modal })); }
    switchTab = (activeTab) => { this.setState({ activeTab }); }

    //canEdit: email templates open in the email editor (base templates open read only there, outside Operations)
    renderSystemTemplates = (templates, canEdit) => {
        if (templates.length === 0 && !this.state.block) {
            return (
                <Col className="text-center text-muted py-5">
                    <i className="fas fa-file-alt fa-2x mb-2 d-block" />
                    <FormattedMessage id="VoucherTemplates.NoSystemTemplates" />
                </Col>
            );
        }

        return templates.map((group, index) => {
            const target = group.variants.find(v => v.active) || group.variants[0];

            return (
                <Col key={index} className='col-4 mb-3'>
                    <Card
                        className="shadow pointer overbglight p-3"
                        onClick={() => this.getTemplateSample(target.id, group.name, group.hasStripo)}
                        body
                    >
                        <Row>
                            <Col><b>{group.name}</b></Col>
                            <Col className="text-right col-auto pl-0">
                                {canEdit ?
                                    <Button
                                        color="link"
                                        className="p-0 mr-2"
                                        title={this.props.intl.formatMessage({ id: 'VoucherTemplates.Edit' })}
                                        onClick={(e) => { e.stopPropagation(); this.props.history.push(`/TemplateDetails/${target.id}`); }}
                                    >
                                        <i className="fas fa-pencil-alt" />
                                    </Button>
                                : ''}
                                <ActiveInactiveStatusBadge status={group.active} />
                            </Col>
                        </Row>
                        <Row>
                            <Col>{group.type}</Col>
                            <Col className="text-right">
                                {group.hasStripo ?
                                    <>
                                        <Badge color="primary" className="mr-1">Stripo</Badge>
                                        {group.variants.filter(v => v.languageCode).map((variant, i) =>
                                            <Badge color="secondary" className="mr-1" key={i}>
                                                <span className={`flag-icon flag-icon-${variant.languageCode === 'EN' ? 'gb' : variant.languageCode.toLowerCase()} mr-1`} />
                                                {variant.languageCode}
                                            </Badge>
                                        )}
                                    </>
                                : ''}
                            </Col>
                        </Row>
                    </Card>
                </Col>
            );
        });
    }

    render() {
        const { templateList, customTemplateList, selectedTemplate, selectedTemplateIsStripo, previewTab, activeTab } = this.state;
        //Email types are designed on Stripo ("New Email Template"), the Voucher type on the voucher designer ("New Voucher")
        //Voucher (pdf) designer follows the VoucherDesigner module rule; the email editor follows the EmailDesigner module rule
        const canDesignVoucher = canUsePdfDesigner();
        const canDesignEmail = canUseEmailDesigner();
        const emailTemplates = templateList.filter(group => EMAIL_TEMPLATE_TYPES.includes(group.type));
        const voucherTemplates = templateList.filter(group => group.type === 'Voucher');

        return (
            <StyledCard
                icon={'fa-regular fa-file'}
                title={'VoucherTemplates.title'}
                help={<FormattedMessage id="VoucherTemplates.Help" />}
                id="voucherTemplates"
                error={this.state.error}
                block={this.state.block && this.state.blockCustom}
            >
                <Row className="mb-2 align-items-center">
                    <Col>
                        <Nav tabs>
                            <NavItem>
                                <NavLink
                                    className={classnames({ active: activeTab === 'email' })}
                                    onClick={() => this.switchTab('email')}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <FormattedMessage id="VoucherTemplates.Tab.Email" />
                                </NavLink>
                            </NavItem>
                            <NavItem>
                                <NavLink
                                    className={classnames({ active: activeTab === 'voucher' })}
                                    onClick={() => this.switchTab('voucher')}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <FormattedMessage id="VoucherTemplates.Tab.Voucher" />
                                </NavLink>
                            </NavItem>
                            {hasPdfDesignerModule() &&
                                <NavItem>
                                    <NavLink
                                        className={classnames({ active: activeTab === 'custom' })}
                                        onClick={() => this.switchTab('custom')}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <FormattedMessage id="VoucherTemplates.Tab.MyTemplates" />
                                        {customTemplateList.length > 0 &&
                                            <span className="ml-1 badge badge-secondary">{customTemplateList.length}</span>
                                        }
                                    </NavLink>
                                </NavItem>
                            }
                        </Nav>
                    </Col>
                    {canDesignVoucher || canDesignEmail ?
                        <Col className="text-right col-auto">
                            <UncontrolledButtonDropdown>
                                <DropdownToggle caret className="btn-host btn-sm">
                                    <i className="fas fa-plus mr-1" />
                                    <FormattedMessage id="VoucherTemplates.NewTemplate" />
                                </DropdownToggle>
                                <DropdownMenu right>
                                    {canDesignVoucher ?
                                        <DropdownItem onClick={() => this.props.history.push('/VoucherTemplateDesigner')}>
                                            <i className="fa-solid fa-ticket mr-2" /><FormattedMessage id="VoucherTemplates.NewVoucher" />
                                        </DropdownItem>
                                    : ''}
                                    {canDesignEmail ?
                                        <DropdownItem onClick={() => this.props.history.push('/TemplateDetails')}>
                                            <i className="fa-solid fa-envelope mr-2" /><FormattedMessage id="VoucherTemplates.NewEmailTemplate" />
                                        </DropdownItem>
                                    : ''}
                                </DropdownMenu>
                            </UncontrolledButtonDropdown>
                        </Col>
                    : ''}
                </Row>

                <TabContent activeTab={activeTab}>
                    <TabPane tabId="email" className="pt-3">
                        <Row>
                            {this.renderSystemTemplates(emailTemplates, canDesignEmail)}
                        </Row>
                    </TabPane>

                    <TabPane tabId="voucher" className="pt-3">
                        <Row>
                            {this.renderSystemTemplates(voucherTemplates)}
                        </Row>
                    </TabPane>

                    <TabPane tabId="custom" className="pt-3">
                        <Row>
                            {customTemplateList.length === 0 && !this.state.blockCustom &&
                                <Col className="text-center text-muted py-5">
                                    <i className="fas fa-file-alt fa-2x mb-2 d-block" />
                                    <FormattedMessage id="VoucherTemplates.NoCustomTemplates" />
                                </Col>
                            }
                            {customTemplateList.map((template, index) =>
                                <Col key={index} className='col-4 mb-3'>
                                    <Card className="shadow p-3" body>
                                        <Row>
                                            <Col><b>{template.name}</b></Col>
                                            <Col className="text-right col-3 pl-0">
                                                <ActiveInactiveStatusBadge status={template.active} />
                                            </Col>
                                        </Row>
                                        <Row className="mt-2 align-items-center">
                                            <Col className="text-muted small">
                                                {template.pageSize} &middot; {template.orientation}
                                            </Col>
                                            {canDesignVoucher &&
                                                <Col className="text-right col-auto">
                                                    <Button
                                                        color="link"
                                                        className="p-0 mr-2"
                                                        title={this.props.intl.formatMessage({ id: 'VoucherTemplates.Edit' })}
                                                        onClick={() => this.props.history.push(`/VoucherTemplateDesigner/${template.id}`)}
                                                    >
                                                        <i className="fas fa-pencil-alt" />
                                                    </Button>
                                                    <Button
                                                        color="link"
                                                        className="p-0 text-danger"
                                                        title={this.props.intl.formatMessage({ id: 'VoucherTemplates.Delete' })}
                                                        onClick={(e) => this.deleteCustomTemplate(template.id, e)}
                                                    >
                                                        <i className="fas fa-trash-alt" />
                                                    </Button>
                                                </Col>
                                            }
                                        </Row>
                                    </Card>
                                </Col>
                            )}
                        </Row>
                    </TabPane>
                </TabContent>

                {this.state.modal &&
                    <Modal isOpen={this.state.modal} toggle={this.toggleModal} fade={false} className="modal-xl">
                        <ModalBody className="p-3">
                            <Row className="mb-3">
                                <Col><h5>{this.state.selectedTemplateName}</h5></Col>
                            </Row>
                            <Nav tabs>
                                {selectedTemplate?.map((template, key) =>
                                    <NavItem key={key}>
                                        <NavLink
                                            className={classnames({ active: previewTab === template.languageCode })}
                                            onClick={() => this.togglePreviewTab(template.languageCode)}
                                        >
                                            <span className={`flag-icon flag-icon-${template.languageCode === 'EN' ? 'gb' : template.languageCode?.toLowerCase()} mb-2 px-2`} />
                                        </NavLink>
                                    </NavItem>
                                )}
                            </Nav>
                            <TabContent activeTab={previewTab}>
                                {selectedTemplate?.map((template, key) =>
                                    <TabPane tabId={template.languageCode} key={key} className="pt-3">
                                        {!selectedTemplateIsStripo ?
                                            ReactHtmlParser(template.template)
                                        : template.isPublished ?
                                            //Stripo html is a full document: sandboxed iframe (no scripts, no same-origin) keeps its styles and the app's apart
                                            <iframe srcDoc={template.template} sandbox="" title={`${this.state.selectedTemplateName} ${template.languageCode}`} className="template-preview-frame" />
                                        :
                                            <div className="text-center text-muted py-5">
                                                <i className="fas fa-eye-slash fa-2x mb-2 d-block" />
                                                <FormattedMessage id="VoucherTemplates.LanguageNotPublished" />
                                            </div>
                                        }
                                    </TabPane>
                                )}
                            </TabContent>
                        </ModalBody>
                    </Modal>
                }
            </StyledCard>
        );
    }
}

export default injectIntl(VoucherTemplates);
